"use client";

import * as React from "react";
import { useStore } from "@/lib/store";
import { TextReader } from "@/components/text-reader";
import { SelectionPanel } from "@/components/selection-panel";
import { SettingsMenu } from "@/components/settings-menu";
import { ShortcutsHelp } from "@/components/shortcuts-help";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import {
  ArrowLeft,
  Settings as SettingsIcon,
  Keyboard,
  Sun,
  Moon,
  Plus,
  Minus,
  Type,
  AlignLeft,
  Pilcrow,
  BookMarked,
} from "lucide-react";
import { useTheme } from "next-themes";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts";
import { useTextToSpeech } from "@/hooks/use-text-to-speech";
import { cn } from "@/lib/utils";

export function ReaderScreen() {
  const goHome = useStore((s) => s.goHome);
  const title = useStore((s) => s.title);
  const parsed = useStore((s) => s.parsed);
  const settings = useStore((s) => s.settings);
  const setSettings = useStore((s) => s.setSettings);
  const selectionMode = useStore((s) => s.selectionMode);
  const setSelectionMode = useStore((s) => s.setSelectionMode);
  const selectedWordId = useStore((s) => s.selectedWordId);
  const vocabulary = useStore((s) => s.vocabulary);
  const { theme, setTheme } = useTheme();
  const { speak, stop, speaking } = useTextToSpeech();

  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false);

  const onSpeak = React.useCallback(() => {
    if (!parsed || !selectedWordId) return;
    if (speaking) {
      stop();
      return;
    }
    // Build text from current selection
    const w = parsed.words.find((x) => x.id === selectedWordId);
    if (!w) return;
    let text = w.text;
    if (selectionMode === "phrase") {
      const s = parsed.sentences.find((x) => x.id === w.sentenceId);
      if (s) text = s.text;
    } else if (selectionMode === "paragraph") {
      const p = parsed.paragraphs.find((x) => x.id === w.paragraphId);
      if (p) text = p.text;
    }
    speak(text);
  }, [parsed, selectedWordId, selectionMode, speaking, speak, stop]);

  const onSaveWord = React.useCallback(() => {
    // Trigger save button click on the selection panel
    const btn = document.querySelector<HTMLButtonElement>(
      '[data-save-word-btn="true"]'
    );
    btn?.click();
  }, []);

  useKeyboardShortcuts({
    scrollContainerRef,
    onOpenSettings: () => setSettingsOpen(true),
    onOpenShortcuts: () => setShortcutsOpen(true),
    onSpeak,
    onSaveWord,
    enabled: !settingsOpen && !shortcutsOpen,
  });

  // Detect mobile / narrow viewport — show a "use desktop" message
  const [tooNarrow, setTooNarrow] = React.useState(false);
  React.useEffect(() => {
    const check = () => setTooNarrow(window.innerWidth < 900);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  if (tooNarrow) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <div className="flex h-16 w-16 mx-auto items-center justify-center rounded-full bg-muted mb-4">
            <Type className="h-7 w-7 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Otimizado para PC</h2>
          <p className="text-sm text-muted-foreground mb-6">
            O ReadFluent foi projetado para telas grandes, com leitor e painel de seleção lado a
            lado. Por favor, use um computador ou amplie esta janela para pelo menos 900 px de
            largura.
          </p>
          <Button variant="outline" onClick={goHome}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Voltar ao início
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Top bar */}
      <header className="shrink-0 border-b bg-background/95 backdrop-blur z-20">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 shrink-0"
              onClick={goHome}
              title="Voltar ao início"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Início</span>
            </Button>
            <div className="w-px h-6 bg-border mx-1" />
            <div className="min-w-0">
              <div className="text-sm font-medium truncate max-w-[40vw]">{title}</div>
              <div className="text-[10px] text-muted-foreground">
                {parsed?.totalWords.toLocaleString()} palavras
              </div>
            </div>
          </div>

          {/* Center: quick mode switcher */}
          <div className="hidden md:flex items-center gap-1 bg-muted/50 rounded-lg p-0.5">
            <ModeChip
              active={selectionMode === "word"}
              onClick={() => setSelectionMode("word")}
              icon={<Type className="h-3.5 w-3.5" />}
              label="Palavra"
            />
            <ModeChip
              active={selectionMode === "phrase"}
              onClick={() => setSelectionMode("phrase")}
              icon={<AlignLeft className="h-3.5 w-3.5" />}
              label="Frase"
            />
            <ModeChip
              active={selectionMode === "paragraph"}
              onClick={() => setSelectionMode("paragraph")}
              icon={<Pilcrow className="h-3.5 w-3.5" />}
              label="Parágrafo"
            />
          </div>

          {/* Right: actions */}
          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0"
                    onClick={() => setSettings({ fontSize: Math.max(14, settings.fontSize - 1) })}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Diminuir fonte (−)</TooltipContent>
              </Tooltip>
              <span className="text-xs tabular-nums w-9 text-center text-muted-foreground">
                {settings.fontSize}
              </span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0"
                    onClick={() => setSettings({ fontSize: Math.min(40, settings.fontSize + 1) })}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Aumentar fonte (+)</TooltipContent>
              </Tooltip>

              <div className="w-px h-6 bg-border mx-1" />

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0"
                    onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                  >
                    {theme === "dark" ? (
                      <Sun className="h-4 w-4" />
                    ) : (
                      <Moon className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Alternar tema (D)</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0"
                    onClick={() => setSettingsOpen(true)}
                  >
                    <SettingsIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Configurações (M)</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0 relative"
                    onClick={() => {
                      // Switch right panel to vocabulary tab by triggering a custom event
                      window.dispatchEvent(new CustomEvent("rf:show-vocabulary"));
                    }}
                  >
                    <BookMarked className="h-4 w-4" />
                    {vocabulary.length > 0 && (
                      <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-primary" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Vocabulário</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 p-0"
                    onClick={() => setShortcutsOpen(true)}
                  >
                    <Keyboard className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Atalhos (?)</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>
      </header>

      {/* Main split view */}
      <div className="flex-1 min-h-0">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          <ResizablePanel defaultSize={62} minSize={40} className="min-w-0">
            <TextReader scrollContainerRef={scrollContainerRef} />
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={38} minSize={28} className="min-w-0 bg-background">
            <SelectionPanel />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      <SettingsMenu open={settingsOpen} onOpenChange={setSettingsOpen} />
      <ShortcutsHelp open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  );
}

function ModeChip({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs transition-colors",
        active
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground"
      )}
    >
      {icon}
      {label}
    </button>
  );
}
